import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  IconButton,
  Button,
  Alert,
  AlertTitle,
  Container,
  Tooltip,
  Collapse,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  TouchApp as TouchAppIcon,
  Block as BlockIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  PhoneDisabled as PhoneDisabledIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, toastError } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import { useQueryClient } from "react-query";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";

/* ─── palette & shared tokens ────────── */
const T = {
  bg: "#f4f6f8",
  surface: "#ffffff",
  surfaceAlt: "#f9fafc",
  border: "#e0e4ec",
  borderHover: "#b0b8c4",
  accent: "#1976d2",
  accentGlow: "rgba(25, 118, 210, 0.12)",
  danger: "#d32f2f",
  dangerGlow: "rgba(211, 47, 47, 0.12)",
  success: "#2e7d32",
  successGlow: "rgba(46, 125, 50, 0.12)",
  textPrimary: "#121926",
  textSecondary: "#697586",
  textMuted: "#9aa4b2",
  dragOver: "rgba(25, 118, 210, 0.08)",
};

const card = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: "16px",
  overflow: "hidden",
  transition: "border-color 0.2s",
  "&:hover": { borderColor: T.borderHover },
};

const INITIAL_SHOW = 8;

/* ─── helpers ──────────────────────────────────────────────── */
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/* ─── sub-components ───────────────────────────────────────── */

const SectionHeader = ({ icon, label, color = T.accent, count }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        background: color === T.danger ? T.dangerGlow : T.accentGlow,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{ color: T.textPrimary, fontFamily: "'DM Sans', sans-serif" }}
    >
      {label}
    </Typography>
    {count !== undefined && (
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          fontSize: "0.68rem",
          fontWeight: 700,
          background: color === T.danger ? T.dangerGlow : T.accentGlow,
          color,
          border: "none",
        }}
      />
    )}
  </Stack>
);

const UsedButtonsPanel = ({ buttons, optOutSet }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? buttons : buttons.slice(0, INITIAL_SHOW);
  const hidden = buttons.length - INITIAL_SHOW;

  return (
    <Box
      sx={{ ...card, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.03)" }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: `1px solid ${T.border}`,
          background: `linear-gradient(135deg, ${T.surfaceAlt} 0%, ${T.surface} 100%)`,
        }}
      >
        <SectionHeader
          icon={<TouchAppIcon fontSize="small" />}
          label="Used Action Buttons"
          count={buttons.length}
        />
        <Typography variant="caption" sx={{ color: T.textSecondary }}>
          Drag any chip → right panel to mark it as an opt-out trigger
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        {buttons.length === 0 ? (
          <Alert
            severity="info"
            sx={{
              background: T.surfaceAlt,
              border: `1px solid ${T.border}`,
              color: T.textSecondary,
              borderRadius: "12px",
              "& .MuiAlert-icon": { color: T.accent },
            }}
          >
            No action buttons found in your message history.
          </Alert>
        ) : (
          <Droppable
            droppableId="usedButtons"
            direction="horizontal"
            isDropDisabled
          >
            {(provided, snapshot) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  minHeight: 48,
                  borderRadius: "12px",
                  padding: "12px",
                  background: snapshot.isDraggingOver
                    ? T.dragOver
                    : "transparent",
                  border: `1px dashed ${
                    snapshot.isDraggingOver ? T.accent : "transparent"
                  }`,
                  transition: "all 0.2s",
                }}
              >
                {visible.map((label, index) => {
                  const isAlreadyAdded = optOutSet.has(label.toLowerCase());
                  return (
                    <Draggable
                      key={`used-${label}`}
                      draggableId={`used-${label}`}
                      index={index}
                      isDragDisabled={isAlreadyAdded}
                    >
                      {(prov, snap) => (
                        <Box
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          sx={{ display: "inline-flex" }}
                        >
                          <Tooltip
                            title={
                              isAlreadyAdded
                                ? "Already in opt-out list"
                                : "Drag to add as opt-out trigger"
                            }
                            arrow
                          >
                            <Chip
                              icon={
                                isAlreadyAdded ? (
                                  <CheckCircleIcon
                                    sx={{ fontSize: "0.85rem !important" }}
                                  />
                                ) : (
                                  <DragIndicatorIcon
                                    sx={{ fontSize: "0.85rem !important" }}
                                  />
                                )
                              }
                              label={label}
                              size="small"
                              sx={{
                                fontFamily: "'DM Mono', monospace",
                                fontSize: "0.78rem",
                                fontWeight: 500,
                                cursor: isAlreadyAdded ? "default" : "grab",
                                background: isAlreadyAdded
                                  ? T.successGlow
                                  : snap.isDragging
                                  ? T.accentGlow
                                  : T.surfaceAlt,
                                color: isAlreadyAdded
                                  ? T.success
                                  : snap.isDragging
                                  ? T.accent
                                  : T.textPrimary,
                                border: `1px solid ${
                                  isAlreadyAdded
                                    ? T.success + "50"
                                    : snap.isDragging
                                    ? T.accent
                                    : T.border
                                }`,
                                transform: snap.isDragging
                                  ? "rotate(3deg) scale(1.05)"
                                  : "none",
                                boxShadow: snap.isDragging
                                  ? `0 8px 24px ${T.accentGlow}`
                                  : "none",
                                transition:
                                  "background 0.15s, color 0.15s, box-shadow 0.15s",
                                "& .MuiChip-icon": {
                                  color: isAlreadyAdded
                                    ? T.success
                                    : T.textMuted,
                                },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}

                {buttons.length > INITIAL_SHOW && (
                  <Button
                    size="small"
                    endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setExpanded((v) => !v)}
                    sx={{
                      fontSize: "0.72rem",
                      color: T.textSecondary,
                      textTransform: "none",
                      "&:hover": { color: T.accent, background: T.accentGlow },
                      borderRadius: "20px",
                      px: 1.5,
                    }}
                  >
                    {expanded ? "Show less" : `+${hidden} more`}
                  </Button>
                )}
              </Box>
            )}
          </Droppable>
        )}
      </Box>
    </Box>
  );
};

const OptOutPanel = ({ optOutButtons, setOptOutButtons, saving, onSave }) => {
  const handleChange = (index, value) => {
    const next = [...optOutButtons];
    next[index] = value;
    setOptOutButtons(next);
  };

  const handleRemove = (index) => {
    if (optOutButtons.length > 1) {
      setOptOutButtons(optOutButtons.filter((_, i) => i !== index));
    } else {
      toast.warning("You must keep at least one opt-out trigger.");
    }
  };

  const handleAdd = () => {
    if (optOutButtons.length < 10) {
      setOptOutButtons([...optOutButtons, ""]);
    } else {
      toast.info("Maximum 10 opt-out triggers allowed.");
    }
  };

  return (
    <Box
      sx={{
        ...card,
        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
        borderColor: `${T.danger}55`,
        height: "100%",
        "&:hover": { borderColor: `${T.danger}99` },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          borderBottom: `1px solid ${T.border}`,
          background: `linear-gradient(135deg, #fff5f5 0%, ${T.surface} 100%)`,
        }}
      >
        <SectionHeader
          icon={<BlockIcon fontSize="small" />}
          label="Opt-Out Triggers"
          color={T.danger}
          count={optOutButtons.filter((b) => b.trim()).length}
        />
        <Typography variant="caption" sx={{ color: T.textSecondary }}>
          Drop chips here from the left — or add custom keywords manually
        </Typography>
      </Box>

      <Box sx={{ p: 3 }}>
        <Alert
          icon={<InfoIcon fontSize="small" />}
          sx={{
            background: T.dangerGlow,
            border: `1px solid rgba(211,47,47,0.25)`,
            borderRadius: "12px",
            color: T.textSecondary,
            mb: 3,
            "& .MuiAlert-icon": { color: T.danger },
          }}
        >
          <AlertTitle
            sx={{ fontWeight: 700, color: T.textPrimary, fontSize: "0.82rem" }}
          >
            Protect your WABA quality rating
          </AlertTitle>
          <Typography variant="caption">
            Clear opt-out keywords like{" "}
            <strong style={{ color: T.textPrimary }}>Stop</strong> or{" "}
            <strong style={{ color: T.textPrimary }}>Unsubscribe</strong> reduce
            spam reports and prevent Meta from restricting or{" "}
            <strong style={{ color: T.danger }}>banning</strong> your number.
          </Typography>
        </Alert>

        <Droppable droppableId="optOutButtons" direction="vertical">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                minHeight: 64,
                borderRadius: "12px",
                background: snapshot.isDraggingOver
                  ? T.dangerGlow
                  : "transparent",
                border: `2px dashed ${
                  snapshot.isDraggingOver ? T.danger : T.border
                }`,
                transition: "all 0.2s",
                p: snapshot.isDraggingOver ? 1 : 0,
                mb: 2,
              }}
            >
              {snapshot.isDraggingOver &&
                optOutButtons.every((b) => b.trim()) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      py: 1.5,
                      color: T.danger,
                    }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                    <Typography variant="caption" fontWeight={600}>
                      Drop to add as opt-out trigger
                    </Typography>
                  </Box>
                )}

              <Stack spacing={1.5} sx={{ p: snapshot.isDraggingOver ? 1 : 0 }}>
                {optOutButtons.map((btn, index) => (
                  <Draggable
                    key={`opt-${index}`}
                    draggableId={`opt-${index}`}
                    index={index}
                  >
                    {(prov, snap) => (
                      <Stack
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                          background: snap.isDragging
                            ? T.surfaceAlt
                            : "transparent",
                          borderRadius: "10px",
                          boxShadow: snap.isDragging
                            ? `0 8px 24px rgba(0,0,0,0.1)`
                            : "none",
                          p: snap.isDragging ? 0.5 : 0,
                          transition: "background 0.15s",
                        }}
                      >
                        <Box
                          {...prov.dragHandleProps}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            color: T.textMuted,
                            cursor: "grab",
                            "&:hover": { color: T.textSecondary },
                            px: 0.5,
                          }}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </Box>

                        <TextField
                          fullWidth
                          size="small"
                          placeholder="e.g., Stop, Unsubscribe, Opt-out"
                          value={btn}
                          onChange={(e) => handleChange(index, e.target.value)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              background: T.surfaceAlt,
                              borderRadius: "10px",
                              color: T.textPrimary,
                              fontFamily: "'DM Mono', monospace",
                              fontSize: "0.82rem",
                              "& fieldset": { borderColor: T.border },
                              "&:hover fieldset": {
                                borderColor: T.borderHover,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: T.danger,
                              },
                            },
                            "& input::placeholder": { color: T.textMuted },
                          }}
                        />

                        <IconButton
                          size="small"
                          onClick={() => handleRemove(index)}
                          disabled={optOutButtons.length === 1}
                          sx={{
                            color: T.textMuted,
                            "&:hover": {
                              color: T.danger,
                              background: T.dangerGlow,
                            },
                            "&.Mui-disabled": { opacity: 0.2 },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            </Box>
          )}
        </Droppable>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            startIcon={<AddIcon />}
            onClick={handleAdd}
            disabled={optOutButtons.length >= 10}
            size="small"
            sx={{
              color: T.textSecondary,
              textTransform: "none",
              fontSize: "0.78rem",
              "&:hover": { color: T.accent, background: T.accentGlow },
              "&.Mui-disabled": { opacity: 0.3 },
            }}
          >
            Add keyword
          </Button>

          <LoadingButton
            variant="contained"
            startIcon={<SaveIcon />}
            loading={saving}
            onClick={onSave}
            disableElevation
            sx={{
              background: `linear-gradient(135deg, ${T.danger} 0%, #b71c1c 100%)`,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "none",
              borderRadius: "10px",
              px: 2.5,
              "&:hover": {
                background: `linear-gradient(135deg, #e53935 0%, ${T.danger} 100%)`,
                boxShadow: `0 4px 20px ${T.dangerGlow}`,
              },
            }}
          >
            Save Opt-Outs
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};

/* ─── main component ───────────────────────────────────────── */
const WhatsAppButtonSettings = ({ embedded = false, onSaved }) => {
  const queryClient = useQueryClient();
  const { data: settings, isLoading: loadingSettings, isError: settingsError, refetch: reloadSettings } = useApiEp("/api/waba-opt-out-settings");

  // Fetch unique buttons and opted-out numbers concurrently
  const { data: usedButtonsData, isLoading: loadingButtons } = useApiEp(
    `/api/get-unique-buttons-waba`
  );
  const { data: optedOutNumbersData, isLoading: loadingOptOutNumbers } =
    useApiEp(`/api/get-opted-out-numbers`);

  const usedButtons = usedButtonsData ?? [];
  const optedOutNumbers = optedOutNumbersData ?? [];
  const [optOutButtons, setOptOutButtons] = useState([]);
  useEffect(() => {
    if (settings) setOptOutButtons(settings.optOutButtons);
  }, [settings]);
  const [saving, setSaving] = useState(false);

  // State for Opt-out Numbers Modal
  const [modalOpen, setModalOpen] = useState(false);

  const optOutSet = new Set(
    optOutButtons.filter((b) => b.trim()).map((b) => b.toLowerCase())
  );

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    if (
      source.droppableId === "optOutButtons" &&
      destination.droppableId === "optOutButtons"
    ) {
      setOptOutButtons(reorder(optOutButtons, source.index, destination.index));
      return;
    }

    if (
      source.droppableId === "usedButtons" &&
      destination.droppableId === "optOutButtons"
    ) {
      const label = draggableId.replace(/^used-/, "");
      if (optOutSet.has(label.toLowerCase())) return;

      const next = [...optOutButtons];
      next.splice(destination.index, 0, label);
      setOptOutButtons(next);
    }
  };

  const handleSave = async () => {
    const validButtons = [...new Set(optOutButtons.map((btn) => btn.trim()).filter(Boolean))];
    if (validButtons.length === 0) {
      toast.warning("Please provide at least one valid opt-out keyword.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_ENDPOINT}/api/save-opt-out-buttons`,
        { optOutButtons: validButtons },
        getHeader()
      );
      await Promise.all([
        queryClient.invalidateQueries([`${API_ENDPOINT}/api/waba-opt-out-settings`]),
        queryClient.invalidateQueries([`${API_ENDPOINT}/api/get-opted-out-numbers`]),
      ]);
      onSaved?.();
      toast.success("WABA triggers saved and unsubscribe list synced!");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyNumbers = () => {
    if (optedOutNumbers.length === 0) return;
    navigator.clipboard.writeText(optedOutNumbers.join("\n"));
    toast.success("Copied to clipboard!");
  };

  if (loadingButtons || loadingSettings) return <MuPb />;
  if (settingsError) return <Alert severity="error" action={<Button onClick={() => reloadSettings()}>Retry</Button>}>Unable to load WABA opt-out triggers.</Alert>;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
      `}</style>

      <Box
        sx={{
          minHeight: embedded ? "auto" : "100vh",
          background: T.bg,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
          <Box sx={{ mb: 3 }}>
            {!embedded && <BackBtn />}
          </Box>

          {/* Header Area with Opt-out Count Option */}
          <Box
            sx={{
              mb: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 1 }}
              >
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{
                    color: T.textPrimary,
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Button & Response Management
                </Typography>
                <Chip
                  label="WABA"
                  size="small"
                  sx={{
                    background: T.accentGlow,
                    color: T.accent,
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.06em",
                    height: 20,
                  }}
                />
              </Stack>
              <Typography
                variant="body2"
                sx={{ color: T.textSecondary, maxWidth: 560 }}
              >
                Review buttons sent to customers and define which replies
                trigger an opt-out — keeping your quality rating healthy.
              </Typography>
            </Box>

            {/* Unsubscribed Numbers Stats Clickable Card */}
            {!loadingOptOutNumbers && (
              <Tooltip
                title="View numbers that have opted out"
                placement="left"
              >
                <Button
                  onClick={() => setModalOpen(true)}
                  sx={{
                    background: T.dangerGlow,
                    border: `1px solid ${T.danger}40`,
                    borderRadius: "12px",
                    px: 3,
                    py: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textTransform: "none",
                    minWidth: "120px",
                    "&:hover": {
                      background: T.dangerGlow,
                      borderColor: T.danger,
                      boxShadow: `0 4px 12px ${T.danger}20`,
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: T.danger, fontWeight: 800, lineHeight: 1.2 }}
                  >
                    {optedOutNumbers.length}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: T.danger, fontWeight: 600 }}
                  >
                    Unsubscribed
                  </Typography>
                </Button>
              </Tooltip>
            )}
          </Box>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gap: 3,
                alignItems: "start",
              }}
            >
              <UsedButtonsPanel buttons={usedButtons} optOutSet={optOutSet} />

              <OptOutPanel
                optOutButtons={optOutButtons}
                setOptOutButtons={setOptOutButtons}
                saving={saving}
                onSave={handleSave}
              />
            </Box>
          </DragDropContext>

          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "12px",
              background: T.surfaceAlt,
              border: `1px solid ${T.border}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <DragIndicatorIcon sx={{ color: T.textMuted, fontSize: "1rem" }} />
            <Typography variant="caption" sx={{ color: T.textMuted }}>
              <strong style={{ color: T.textPrimary }}>Pro tip:</strong> Drag
              chips from{" "}
              <strong style={{ color: T.accent }}>Used Action Buttons</strong>{" "}
              directly onto the{" "}
              <strong style={{ color: T.danger }}>Opt-Out Triggers</strong>{" "}
              panel to instantly add them. You can also reorder the opt-out list
              by dragging rows up or down.
            </Typography>
          </Box>
        </Container>

        {/* Modal for Unsubscribed Numbers */}
        <Dialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "16px", background: T.surface },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `1px solid ${T.border}`,
              p: 3,
              pb: 2,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: T.dangerGlow,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PhoneDisabledIcon
                  sx={{ color: T.danger, fontSize: "1.2rem" }}
                />
              </Box>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Unsubscribed Numbers
              </Typography>
            </Stack>
            <IconButton
              onClick={() => setModalOpen(false)}
              size="small"
              sx={{ color: T.textSecondary }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0, maxHeight: "400px" }}>
            {optedOutNumbers.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ color: T.textSecondary }}>
                  Good news! No customers have opted out yet.
                </Typography>
              </Box>
            ) : (
              <List sx={{ width: "100%", p: 0 }}>
                {optedOutNumbers.map((num, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      borderBottom: `1px solid ${T.border}`,
                      px: 3,
                      py: 1.5,
                      "&:hover": { background: T.surfaceAlt },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PhoneDisabledIcon
                        fontSize="small"
                        sx={{ color: T.textMuted }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontFamily: "'DM Mono', monospace",
                            fontWeight: 500,
                            color: T.textPrimary,
                          }}
                        >
                          {num}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              p: 2.5,
              pt: 2,
              borderTop: `1px solid ${T.border}`,
              background: T.surfaceAlt,
            }}
          >
            <Button
              onClick={handleCopyNumbers}
              startIcon={<ContentCopyIcon />}
              disabled={optedOutNumbers.length === 0}
              sx={{
                color: T.textSecondary,
                textTransform: "none",
                fontWeight: 600,
                mr: "auto",
              }}
            >
              Copy All
            </Button>
            <Button
              onClick={() => setModalOpen(false)}
              variant="contained"
              disableElevation
              sx={{
                background: T.textPrimary,
                color: "#fff",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { background: "#000" },
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default WhatsAppButtonSettings;
